import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useAgents } from "@/components/Agents/AgentsContext";
import { SCHEDULE_CADENCE_OPTIONS, type ScheduleCadence } from "@/lib/agentsDemoData";
import { agentsHref } from "@/lib/atwAgentsHost";

export function CreateSchedulePage() {
  const { may, viewOnly, addSchedule } = useAgents();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [cadence, setCadence] = useState<ScheduleCadence>("business_hours");
  const [note, setNote] = useState("");
  const canCreate = may("manage_schedules");

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!canCreate) return;
    const id = addSchedule({ name, cadence, note });
    if (!id) {
      toast.error("Enter a schedule name.");
      return;
    }
    toast.success(`${name.trim()} added.`);
    navigate(agentsHref("/schedules"));
  };

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={agentsHref("/schedules")}
          className="mb-2 inline-flex items-center gap-1 text-sm text-gray-600 hover:underline dark:text-gray-400"
        >
          <ArrowLeft className="size-4" aria-hidden />
          Schedules
        </Link>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Add a schedule</h1>
        <p className="mt-1 max-w-2xl text-sm text-gray-600 dark:text-gray-400">
          Name the clock and choose when it runs. Saving it here keeps it on this organization so
          you can assign it to agents.
        </p>
      </div>

      {viewOnly ? (
        <p className="max-w-2xl rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-100">
          Standard role is view only. Ask a Super Admin to add a schedule.
        </p>
      ) : null}

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle className="text-base">New schedule</CardTitle>
          <CardDescription>The note is optional. Saving stores the schedule on this organization.</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={submit}>
            <label className="block text-sm">
              <span className="font-medium text-gray-700 dark:text-gray-300">Name</span>
              <Input
                className="mt-1 w-full"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Weekday hours"
                disabled={!canCreate}
                autoComplete="off"
                required
              />
            </label>
            <fieldset className="space-y-2" disabled={!canCreate}>
              <legend className="text-sm font-medium text-gray-700 dark:text-gray-300">
                When it runs
              </legend>
              {SCHEDULE_CADENCE_OPTIONS.map((option) => (
                <label key={option.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="schedule-cadence"
                    value={option.id}
                    checked={cadence === option.id}
                    onChange={() => setCadence(option.id)}
                    disabled={!canCreate}
                  />
                  <span className="text-gray-800 dark:text-gray-200">{option.label}</span>
                </label>
              ))}
            </fieldset>
            <label className="block text-sm">
              <span className="font-medium text-gray-700 dark:text-gray-300">Note</span>
              <Input
                className="mt-1 w-full"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Optional"
                disabled={!canCreate}
                autoComplete="off"
              />
            </label>
            <div className="flex flex-wrap justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => navigate(agentsHref("/schedules"))}>
                Cancel
              </Button>
              <Button type="submit" disabled={!canCreate}>
                Save schedule
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
